"use client";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

import Image from "next/image";

interface MissionVisionImage {
  src?: string;
  alt?: string;
}

interface MissionVisionSection {
  title?: string;
  description?: string;
  image?: MissionVisionImage;
}

interface StatisticsSection extends MissionVisionSection {
  percentage?: string;
  label?: string;
}

interface MissionVisionData {
  tagline?: string;
  title?: string;
  description?: string;
  sections: {
    vision?: MissionVisionSection;
    statistics?: StatisticsSection;
    mission?: MissionVisionSection;
  };
}

interface MissionVisionProps {
  data: MissionVisionData;
}

export default function MissionVision({ data }: MissionVisionProps) {
  const { tagline, title, description, sections } = data;

  const { vision, statistics, mission } = sections;

  const headerRef = useRef<HTMLDivElement>(null);
  const visionRef = useRef<HTMLDivElement>(null);
  const statisticsRef = useRef<HTMLDivElement>(null);
  const missionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !headerRef.current ||
      !visionRef.current ||
      !statisticsRef.current ||
      !missionRef.current
    )
      return;
    const elementsToAnimate = [
      visionRef.current,
      statisticsRef.current,
      missionRef.current,
    ].filter(Boolean);

    const elements = headerRef.current?.querySelectorAll("[data-animate]");
    const ctx = gsap.context(() => {
      if (elements) {
        gsap.fromTo(
          elements,
          {
            opacity: 0,
            y: 30,
            filter: "blur(8px)",
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            filter: "blur(0px)",
            ease: "power2.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      if (elementsToAnimate.length > 0) {
        gsap.set(elementsToAnimate, {
          opacity: 0,
          y: 40,
        });
      }

      gsap.to(visionRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: visionRef.current,
          start: "top 75%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.to(statisticsRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: statisticsRef.current,
          start: "top 75%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.to(missionRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.4,
        ease: "power2.out",
        scrollTrigger: {
          trigger: missionRef.current,
          start: "top 75%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });
    }, [headerRef, visionRef, statisticsRef, missionRef]);

    return () => ctx.revert();
  }, []);

  return (
    <section className="container py-[70px]">
      <div ref={headerRef} className="text-center  mb-16">
        {tagline && (
          <div
            data-animate
            className="flex items-center justify-center mb-4 opacity-0"
          >
            <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
            <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
              {tagline}
            </p>
          </div>
        )}

        {title && (
          <h2
            data-animate
            className="text-4xl md:text-5xl font-bold text-theme-secondary capitalize  opacity-0"
          >
            {title}
          </h2>
        )}
        {description && (
          <p
            data-animate
            className="mt-4 text-lg text-theme-secondary-light max-w-[432px] mx-auto  opacity-0"
          >
            {description}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start ">
        {}
        {vision && (
          <div
            ref={visionRef}
            className="gap-5 flex flex-col sm:flex-row lg:flex-col  sm:max-h-[364px] lg:max-h-full opacity-0 "
          >
            {vision.image?.src && (
              <div className="overflow-hidden sm:w-[50%] rounded-[20px] shadow-lg hover:shadow-2xl transition-shadow duration-300 lg:size-full">
                <Image
                  src={vision.image.src}
                  alt={vision.image.alt || "Vision image"}
                  width={600}
                  height={400}
                  quality={85}
                  className="!size-full  max-h-[364px] object-cover"
                  loading="lazy"
                />
              </div>
            )}
            <div className=" sm:w-[50%] lg:w-full px-[28px] xl:px-[33px] py-5 rounded-[8px] flex flex-col justify-center gap-4  bg-[#FFFAF5]">
              {vision.title && (
                <h2 className="text-primary text-xl font-semibold leading-[36px]">
                  {vision.title}
                </h2>
              )}
              {vision.description && (
                <p className="text-[#4F5051] font-medium leading-[24px] text-[14px] xl:text-[16px] ">
                  {vision.description}
                </p>
              )}
            </div>
          </div>
        )}

        {}
        {statistics && (
          <div
            ref={statisticsRef}
            className="items-stretch !size-full opacity-0"
          >
            {statistics.image?.src && (
              <div className="relative overflow-hidden rounded-[20px] shadow-lg hover:shadow-2xl transition-shadow duration-300 max-xl:h-full !size-full ">
                <div className="size-full relative">
                  <Image
                    src={statistics.image.src}
                    alt={statistics.image.alt || "Statistics image"}
                    width={600}
                    height={1000}
                    quality={85}
                    className="!size-full max-h-[364px] lg:max-h-full object-cover  object-center rounded-xl "
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_56%,rgba(0,0,0,0.5)_77%)]"></div>
                </div>
                <div className="flex flex-col gap-4 absolute inset-0 h-full justify-between py-[32.5px] px-6">
                  {(statistics.percentage || statistics.label) && (
                    <div className=" text-white text-[28px] leading-[32px] tracking-[-0.84px]">
                      {statistics.percentage && (
                        <h3 className="  ">{statistics.percentage}</h3>
                      )}
                      {statistics.label && (
                        <p className="">{statistics.label}</p>
                      )}
                    </div>
                  )}
                  {statistics.description && (
                    <p className="  text-white text-[18px] font-bold max-w-xs leading-[23px] tracking-[-0.54px] ">
                      {statistics.description}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {}
        {mission && (
          <div
            ref={missionRef}
            className="gap-5 flex flex-col sm:flex-row lg:flex-col sm:max-h-[370px] justify-between lg:max-h-full size-full opacity-0"
          >
            <div className="sm:w-[50%] lg:w-full px-[28px] xl:px-[33px] py-5 rounded-[8px] flex flex-col justify-center gap-4  bg-[#FBFCFC] ">
              {mission.title && (
                <h2 className="text-primary text-xl font-semibold leading-[36px]">
                  {mission.title}
                </h2>
              )}
              {mission.description && (
                <p className="text-[#4F5051] font-medium leading-[24px] text-[14px] xl:text-[16px] ">
                  {mission.description}
                </p>
              )}
            </div>

            {mission.image?.src && (
              <div className="sm:w-[50%] lg:w-full overflow-hidden rounded-[20px] shadow-lg hover:shadow-2xl transition-shadow duration-300 size-full  ">
                <Image
                  src={mission.image.src}
                  alt={mission.image.alt || "Mission image"}
                  width={600}
                  height={400}
                  quality={85}
                  className="!size-full  sm:max-h-[370px] lg:max-h-full object-cover object-center "
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
